import type { SuggestionKeyDownProps } from '@tiptap/suggestion';
import {
  useCallback,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';
import styled from '@emotion/styled';

import { shadowLight } from 'app/colors';
import KizenTypography from '__app/kizentypo';
import Menu, { MenuList } from '__components/Kizen/Menu';
import CommentListItem from '__components/Kizen/List/CommentListItem';
import UUID from 'utility/UUID';
import { truncateCss, textCss } from 'app/typography';
import { isNonComposingEnterKeyboardEvent } from 'utility/keyboard';

export type SuggestionListHandle = {
  onKeyDown(props: SuggestionKeyDownProps): boolean;
};

type HeightItem = {
  height: number;
  top: number;
  bottom: number;
};

type Category<T> = {
  label: string;
  items: T[];
  key: string;
};

type NormalItem = { id: string; label: string };

type MergeFieldItem = {
  label: string;
  relationship: string;
};

type Item = NormalItem | MergeFieldItem;

type HeaderItem = { isHeader: true; label: string; key: string };

type InternalItem = HeaderItem | Item;

type SuggestionListProps = {
  items: Item[] | Category<NormalItem>[] | Category<MergeFieldItem>[];
  isLoading?: boolean;
  topButton?: boolean;
  onSelect(item: Item): void;
};

const DefaultWidth = 200;

const StyledMenu = styled(Menu)`
  max-height: 200px;
`;

const EmptyCommentListItem = styled.div<{ focused: boolean }>`
  line-height: 32px;
  ${truncateCss};
  text-align: center;
  padding: 0 12px;
  ${textCss()};
`;
const Loader = styled.div`
  ${shadowLight};
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: ${DefaultWidth}px;
  height: 36px;
  margin: 1px;
  padding: 0 10px;
  background-color: var(--color-background-white);
`;

const LoadingText = styled(KizenTypography)`
  color: var(--color-font-primary);
  margin-left: 10px;
`;

const CommentListHeader = styled(CommentListItem)`
  color: var(--color-font-placeholder);
  font-weight: 700;
`;

const isCategorizedItems = (items: any): items is Category<any>[] => {
  return Array.isArray(items?.[0]?.items);
};

const isHeaderItem = (item: InternalItem): item is HeaderItem => {
  return 'isHeader' in item && item.isHeader;
};

/**
 * @remarks The component will render an unordered list or a description list depending on the
 * structure of the `items` prop. In both cases, items must be an array of objects. If those
 * objects contain an `items` attribute the component will be a description list. Headers and
 * item text are both specified via the `label` attribute.
 *
 *  - ul --> items = `[{ label: 'Item 1' }, [{ label: 'Item 2' }]]`
 *  - dl --> items = `[{ label: 'Header 1', items: [{ label: 'Item 1 ' }]}]`
 */
export const SuggestionList = forwardRef<
  SuggestionListHandle,
  SuggestionListProps
>((props, ref) => {
  const {
    onSelect,
    items: itemsProp,
    topButton = false,
    isLoading = false,
  } = props;

  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const heightsRef = useRef<HeightItem[]>([]);
  const hasHeaders = isCategorizedItems(itemsProp);

  const menuRefCallback = useCallback(
    (node: HTMLDivElement) => {
      if (node) {
        menuRef.current = node;

        const listContainerTagName = hasHeaders ? 'dl' : 'ul';
        const container = node.querySelector(listContainerTagName);

        if (container?.children) {
          let top = 0;
          const heights: HeightItem[] = [];

          for (const child of container.children as HTMLCollectionOf<HTMLElement>) {
            const height = child.offsetHeight;
            const bottom = top + height;
            heights.push({ height, top, bottom });
            top = bottom;
          }

          heightsRef.current = heights;
        }
      }
    },
    [hasHeaders]
  );

  // Force the parent MenuList to remount every time the list changes. Without this, the contents
  // don't update when used in a modal
  const [items, menuListKey] = useMemo<
    [InternalItem[] | Item[], string]
  >(() => {
    if (!hasHeaders) {
      return [itemsProp, UUID.generate()];
    }

    const list = itemsProp.reduce<InternalItem[]>((acc, section) => {
      acc.push({
        label: section.label,
        key: UUID.generate(),
        isHeader: true,
      });

      acc.push(...section.items);

      return acc;
    }, []);

    return [list, UUID.generate()];
  }, [itemsProp, hasHeaders]);

  const trackUp = (index: number) => {
    if (heightsRef.current[index] && menuRef.current) {
      const { height, top } = heightsRef.current[index];
      const topHorrizon = menuRef.current?.scrollTop + 30;

      if (top <= topHorrizon) {
        menuRef.current.scrollTop = menuRef.current?.scrollTop - height;
      }
    }
  };

  const trackDown = (index: number) => {
    if (heightsRef.current[index] && menuRef.current) {
      const { height, bottom } = heightsRef.current[index];
      const bottomHorrizon = menuRef.current?.offsetHeight - 30;

      if (bottom >= bottomHorrizon) {
        menuRef.current.scrollTop = menuRef.current.scrollTop + height;
      }
    }
  };

  const selectItem = (index: number) => {
    const item = items[index];

    // although not being highlighted, enter can still pressed on a header.
    if (item && !isHeaderItem(item)) {
      onSelect(item);
    }
  };

  const upHandler = () => {
    if (selectedIndex > 0) {
      const index = selectedIndex - 1;
      setSelectedIndex(index);
      trackUp(index);
    }
  };

  const downHandler = () => {
    if (selectedIndex < items.length - 1) {
      const index = selectedIndex + 1;
      setSelectedIndex(index);
      trackDown(index);
    }
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  const onMouseOver = (index: number) => {
    setSelectedIndex(index % items.length);
  };

  useEffect(() => {
    setSelectedIndex(0);

    if (menuRef.current?.scrollTop) {
      menuRef.current.scrollTop = 0;
    }
  }, [items]);

  useImperativeHandle(ref, () => {
    return {
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          upHandler();

          return true;
        }

        if (event.key === 'ArrowDown') {
          downHandler();

          return true;
        }

        if (isNonComposingEnterKeyboardEvent(event)) {
          if (items.length) {
            enterHandler();

            return true;
          }

          return false;
        }

        return false;
      },
    };
  });

  return (
    <StyledMenu>
      {items.length || isLoading ? (
        <MenuList
          topButton={topButton}
          key={menuListKey}
          ref={menuRefCallback}
          {...(hasHeaders && { as: 'dl' })}
        >
          {isLoading && (
            <Loader>
              <ClipLoader
                loading
                size={16}
                color="var(--color-viz-cat-2-primary)"
              />
              <LoadingText>{t('Loading Options')}</LoadingText>
            </Loader>
          )}
          {!isLoading &&
            (items || []).map((item, index) => {
              if (isHeaderItem(item)) {
                return (
                  <CommentListHeader as="dt" key={item.key || item.label}>
                    {item.label}
                  </CommentListHeader>
                );
              }

              const key = 'relationship' in item ? item.relationship : item.id;

              return (
                <CommentListItem
                  {...(hasHeaders && { as: 'dd' })}
                  key={key}
                  focused={index === selectedIndex}
                  onClick={() => selectItem(index)}
                  onMouseOver={() => onMouseOver(index)}
                >
                  {item.label}
                </CommentListItem>
              );
            })}
        </MenuList>
      ) : (
        <EmptyCommentListItem key={'no-options'} focused={false}>
          {t('No Options')}
        </EmptyCommentListItem>
      )}
    </StyledMenu>
  );
});
